import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './RestaurantPage.css';
import {
  BarChart3,
  Bell,
  MoveVertical,
  ArrowRight,
  Brain,
  Clock,
  Eye,
  TrendingDown,
  Users,
  MessageSquare,
  Zap,
  EyeOff,
  Trash2,
  Frown
} from 'lucide-react';


function RestaurantPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="ru-page-wrapper">
      {/* 1. HERO */}
      <section className="ru-hero">
        <div className="ru-container">
          <div className="ru-hero-content">
            <span className="ru-kicker">ESPACE ÉTABLISSEMENT</span>
            <h1 className="ru-main-title">
              Reprenez le contrôle <br />
              de vos <span className="highlight-dark">flux étudiants.</span>
            </h1>
            <p className="ru-main-desc">
              Un tableau de bord en temps réel, des prédictions IA et un canal direct vers vos étudiants. Tout ce qu'il faut pour lisser l'affluence et en finir avec les files d'attente.
            </p>
            <div className="ru-hero-actions">
              <Link to="/etablissement/demande-acces-partenaire" className="btn-brutal inverse-primary">
                Demander un accès <ArrowRight size={20} />
              </Link>
              <Link to="/etablissement/login" className="btn-brutal secondary">
                Déjà partenaire ? Se connecter
              </Link>
            </div>

            <div className="ru-hero-metrics">
              <div className="ru-metric">
                <strong>5 000+</strong>
                <span>étudiants actifs</span>
              </div>
              <div className="ru-metric-divider" />
              <div className="ru-metric">
                <strong>200+</strong>
                <span>restaurants suivis</span>
              </div>
              <div className="ru-metric-divider" />
              <div className="ru-metric">
                <strong>−30%</strong>
                <span>de temps d'attente</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. PROBLÈME */}
      <section className="ru-problem">
        <div className="ru-container">
          <div className="ru-text-center">
            <span className="ru-kicker">LE CONSTAT</span>
            <h2 className="ru-section-title">Vous gérez à l'aveugle.<br /><span className="ru-title-accent">Vos étudiants aussi.</span></h2>
            <p className="ru-section-desc">
              Sans donnée fiable, impossible d'anticiper les pics, d'ajuster les stocks ou de communiquer en temps réel. Résultat : surcharge, gaspillage et frustration des deux côtés.
            </p>
          </div>

          <div className="ru-pain-grid">
            <div className="ru-pain-card">
              <div className="ru-pain-icon"><EyeOff size={32} /></div>
              <h3>Aucune visibilité</h3>
              <p>Vous ne savez pas combien de personnes arrivent dans 30 minutes.</p>
            </div>
            <div className="ru-pain-card">
              <div className="ru-pain-icon"><Trash2 size={32} /></div>
              <h3>Gaspillage alimentaire</h3>
              <p>Trop préparé ou pas assez — sans prédiction, c'est du pile ou face.</p>
            </div>
            <div className="ru-pain-card">
              <div className="ru-pain-icon"><Frown size={32} /></div>
              <h3>Étudiants frustrés</h3>
              <p>Files interminables, plats épuisés, aucun moyen de les prévenir.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 3. SOLUTION — FEATURES DÉTAILLÉES */}
      <section className="ru-features">
        <div className="ru-container">
          <div className="ru-text-center">
            <span className="ru-kicker">LA SOLUTION</span>
            <h2 className="ru-section-title">Votre tour de contrôle, <span className="ru-title-accent">en un coup d'œil.</span></h2>
            <p className="ru-section-desc">Six outils pensés pour les gestionnaires de RU. Zéro complexité.</p>
          </div>

          <div className="ru-features-grid">
            <div className="ru-feature-card featured">
              <div className="ru-feature-icon"><Eye size={28} /></div>
              <h3>Radar temps réel</h3>
              <p>Carte interactive avec marqueurs animés. Voyez l'affluence de chaque RU en direct, les tendances et les pings étudiants.</p>
              <span className="ru-feature-tag">Live</span>
            </div>

            <div className="ru-feature-card">
              <div className="ru-feature-icon"><Brain size={28} /></div>
              <h3>Prédictions IA</h3>
              <p>Anticipez les 8 prochaines heures grâce à l'intelligence artificielle. Indice de confiance inclus.</p>
              <span className="ru-feature-tag">Gemini</span>
            </div>

            <div className="ru-feature-card">
              <div className="ru-feature-icon"><MoveVertical size={28} /></div>
              <h3>Override instantané</h3>
              <p>Forcez un statut (Vide, Moyen, Blindé, Fermé) visible immédiatement par tous les étudiants. Ajoutez un message de 280 caractères.</p>
              <span className="ru-feature-tag">Contrôle</span>
            </div>

            <div className="ru-feature-card">
              <div className="ru-feature-icon"><BarChart3 size={28} /></div>
              <h3>Statistiques historiques</h3>
              <p>Graphiques d'affluence sur 7, 30 ou 90 jours. Identifiez vos heures de pointe et comparez vos restaurants.</p>
              <span className="ru-feature-tag">Analytics</span>
            </div>

            <div className="ru-feature-card">
              <div className="ru-feature-icon"><Clock size={28} /></div>
              <h3>Temps d'attente ajusté</h3>
              <p>Définissez un temps de base, les votes étudiants l'affinent en direct. Transparence totale sur l'attente réelle.</p>
              <span className="ru-feature-tag">Collaboratif</span>
            </div>

            <div className="ru-feature-card">
              <div className="ru-feature-icon"><MessageSquare size={28} /></div>
              <h3>Avis repas</h3>
              <p>Consultez les retours étudiants sur la qualité : notes, tags fréquents et satisfaction par restaurant.</p>
              <span className="ru-feature-tag">Feedback</span>
            </div>
          </div>
        </div>
      </section>

      {/* 4. CARTE LIVE DÉMO */}
      <section className="ru-live-map">
        <div className="ru-container">
          <div className="ru-text-center">
            <span className="ru-kicker">EN DIRECT</span>
            <h2 className="ru-section-title">L'affluence, <span className="ru-title-accent">là, maintenant.</span></h2>
            <p className="ru-section-desc">
              Voici les données réelles de nos restaurants partenaires. Chaque point pulse en fonction des signalements étudiants des 15 dernières minutes.
            </p>
          </div>
          <div className="ru-static-map">
            <svg viewBox="0 0 800 780" className="ru-france-svg" aria-label="Carte des restaurants CrousRadar en France">
              <path className="france-outline" d="M390 30 L480 45 L540 80 L590 60 L620 90 L680 120 L720 100 L750 140 L740 200 L760 250 L730 310 L740 370 L710 420 L730 480 L700 530 L720 580 L680 620 L640 600 L600 640 L540 660 L500 700 L440 720 L380 700 L320 740 L280 710 L240 720 L200 680 L160 640 L120 590 L80 520 L60 460 L70 400 L50 340 L80 280 L110 230 L100 180 L130 130 L180 100 L230 70 L290 50 L340 35Z" />
              {/* Paris */}
              <circle cx="420" cy="220" r="8" className="map-dot dot-busy"><animate attributeName="r" values="8;14;8" dur="2s" repeatCount="indefinite"/></circle>
              <circle cx="420" cy="220" r="20" className="map-pulse pulse-busy"><animate attributeName="r" values="20;32;20" dur="2s" repeatCount="indefinite"/><animate attributeName="opacity" values="0.4;0;0.4" dur="2s" repeatCount="indefinite"/></circle>
              <text x="420" y="200" className="map-label">Paris</text>
              {/* Lyon */}
              <circle cx="510" cy="440" r="7" className="map-dot dot-medium"><animate attributeName="r" values="7;12;7" dur="2.3s" repeatCount="indefinite"/></circle>
              <circle cx="510" cy="440" r="18" className="map-pulse pulse-medium"><animate attributeName="r" values="18;28;18" dur="2.3s" repeatCount="indefinite"/><animate attributeName="opacity" values="0.4;0;0.4" dur="2.3s" repeatCount="indefinite"/></circle>
              <text x="510" y="420" className="map-label">Lyon</text>
              {/* Toulouse */}
              <circle cx="350" cy="600" r="7" className="map-dot dot-quiet"><animate attributeName="r" values="7;12;7" dur="2.5s" repeatCount="indefinite"/></circle>
              <circle cx="350" cy="600" r="18" className="map-pulse pulse-quiet"><animate attributeName="r" values="18;28;18" dur="2.5s" repeatCount="indefinite"/><animate attributeName="opacity" values="0.4;0;0.4" dur="2.5s" repeatCount="indefinite"/></circle>
              <text x="350" y="580" className="map-label">Toulouse</text>
              {/* Bordeaux */}
              <circle cx="230" cy="520" r="6" className="map-dot dot-medium"><animate attributeName="r" values="6;11;6" dur="2.1s" repeatCount="indefinite"/></circle>
              <circle cx="230" cy="520" r="16" className="map-pulse pulse-medium"><animate attributeName="r" values="16;26;16" dur="2.1s" repeatCount="indefinite"/><animate attributeName="opacity" values="0.4;0;0.4" dur="2.1s" repeatCount="indefinite"/></circle>
              <text x="230" y="500" className="map-label">Bordeaux</text>
              {/* Nantes */}
              <circle cx="200" cy="360" r="6" className="map-dot dot-quiet"><animate attributeName="r" values="6;11;6" dur="2.4s" repeatCount="indefinite"/></circle>
              <circle cx="200" cy="360" r="16" className="map-pulse pulse-quiet"><animate attributeName="r" values="16;26;16" dur="2.4s" repeatCount="indefinite"/><animate attributeName="opacity" values="0.4;0;0.4" dur="2.4s" repeatCount="indefinite"/></circle>
              <text x="200" y="340" className="map-label">Nantes</text>
              {/* Montpellier */}
              <circle cx="480" cy="600" r="6" className="map-dot dot-busy"><animate attributeName="r" values="6;11;6" dur="1.9s" repeatCount="indefinite"/></circle>
              <circle cx="480" cy="600" r="16" className="map-pulse pulse-busy"><animate attributeName="r" values="16;26;16" dur="1.9s" repeatCount="indefinite"/><animate attributeName="opacity" values="0.4;0;0.4" dur="1.9s" repeatCount="indefinite"/></circle>
              <text x="480" y="580" className="map-label">Montpellier</text>
              {/* Lille */}
              <circle cx="430" cy="110" r="6" className="map-dot dot-medium"><animate attributeName="r" values="6;11;6" dur="2.2s" repeatCount="indefinite"/></circle>
              <circle cx="430" cy="110" r="16" className="map-pulse pulse-medium"><animate attributeName="r" values="16;26;16" dur="2.2s" repeatCount="indefinite"/><animate attributeName="opacity" values="0.4;0;0.4" dur="2.2s" repeatCount="indefinite"/></circle>
              <text x="430" y="90" className="map-label">Lille</text>
              {/* Strasbourg */}
              <circle cx="640" cy="230" r="5" className="map-dot dot-quiet"><animate attributeName="r" values="5;10;5" dur="2.6s" repeatCount="indefinite"/></circle>
              <circle cx="640" cy="230" r="14" className="map-pulse pulse-quiet"><animate attributeName="r" values="14;24;14" dur="2.6s" repeatCount="indefinite"/><animate attributeName="opacity" values="0.4;0;0.4" dur="2.6s" repeatCount="indefinite"/></circle>
              <text x="640" y="210" className="map-label">Strasbourg</text>
              {/* Rennes */}
              <circle cx="220" cy="270" r="5" className="map-dot dot-busy"><animate attributeName="r" values="5;10;5" dur="2s" repeatCount="indefinite"/></circle>
              <circle cx="220" cy="270" r="14" className="map-pulse pulse-busy"><animate attributeName="r" values="14;24;14" dur="2s" repeatCount="indefinite"/><animate attributeName="opacity" values="0.4;0;0.4" dur="2s" repeatCount="indefinite"/></circle>
              <text x="220" y="250" className="map-label">Rennes</text>
              {/* Marseille */}
              <circle cx="560" cy="620" r="7" className="map-dot dot-busy"><animate attributeName="r" values="7;12;7" dur="1.8s" repeatCount="indefinite"/></circle>
              <circle cx="560" cy="620" r="18" className="map-pulse pulse-busy"><animate attributeName="r" values="18;28;18" dur="1.8s" repeatCount="indefinite"/><animate attributeName="opacity" values="0.4;0;0.4" dur="1.8s" repeatCount="indefinite"/></circle>
              <text x="560" y="600" className="map-label">Marseille</text>
            </svg>
            <div className="ru-map-legend">
              <div className="legend-item"><span className="legend-dot legend-busy"></span> Blindé</div>
              <div className="legend-item"><span className="legend-dot legend-medium"></span> Moyen</div>
              <div className="legend-item"><span className="legend-dot legend-quiet"></span> Calme</div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. COMMENT ÇA MARCHE */}
      <section className="ru-how">
        <div className="ru-container">
          <div className="ru-text-center">
            <span className="ru-kicker">EN 3 ÉTAPES</span>
            <h2 className="ru-section-title">Opérationnel en <span className="ru-title-accent">48 heures.</span></h2>
          </div>

          <div className="ru-steps">
            <div className="ru-step">
              <div className="ru-step-num">1</div>
              <h3>Demandez votre accès</h3>
              <p>Remplissez le formulaire avec vos infos et sélectionnez vos RU. C'est gratuit.</p>
            </div>
            <div className="ru-step-arrow"><ArrowRight size={24} /></div>
            <div className="ru-step">
              <div className="ru-step-num">2</div>
              <h3>Activation sous 48h</h3>
              <p>Notre équipe valide votre demande et vous envoie un code d'activation par email.</p>
            </div>
            <div className="ru-step-arrow"><ArrowRight size={24} /></div>
            <div className="ru-step">
              <div className="ru-step-num">3</div>
              <h3>Pilotez vos flux</h3>
              <p>Accédez au dashboard, consultez vos données et communiquez avec vos étudiants.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 5. SCREENSHOTS DU DASHBOARD */}
      <section className="ru-app-screens">
        <div className="ru-container">
          <div className="ru-screens-header">
            <span className="ru-kicker">APERÇU</span>
            <h2 className="ru-section-title">Ce que voient <span className="ru-title-accent">vos étudiants.</span></h2>
            <p className="ru-section-desc">L'app utilisée par 5 000+ étudiants chaque jour, votre vitrine directe.</p>
          </div>

          <div className="ru-screens-gallery">
            <div className="screen-wrapper sw-1">
              <img src="/1.png" alt="Carte d'affluence CrousRadar" />
              <div className="screen-label">Menu Application</div>
            </div>
            <div className="screen-wrapper sw-2">
              <img src="/2.png" alt="Carte d'affluence CrousRadar" />
              <div className="screen-label">Carte Live</div>
            </div>
            <div className="screen-wrapper sw-3">
              <img src="/3.png" alt="Fiche restaurant CrousRadar" />
              <div className="screen-label">Fiche RU</div>
            </div>
            <div className="screen-wrapper sw-4">
              <img src="/leaderboard-screen.png" alt="Classement CrousRadar" />
              <div className="screen-label">Classement</div>
            </div>
          </div>
        </div>
      </section>

      {/* 6. AVANTAGES CONCRETS */}
      <section className="ru-benefits">
        <div className="ru-container">
          <div className="ru-text-center">
            <span className="ru-kicker">RÉSULTATS</span>
            <h2 className="ru-section-title">Ce que nos partenaires <span className="ru-title-accent">constatent.</span></h2>
          </div>

          <div className="ru-benefits-grid">
            <div className="ru-benefit">
              <TrendingDown size={32} className="ru-benefit-icon" />
              <strong>−30% d'attente</strong>
              <p>Les étudiants décalent naturellement quand ils voient « Blindé » en temps réel.</p>
            </div>
            <div className="ru-benefit">
              <Users size={32} className="ru-benefit-icon" />
              <strong>Flux lissés</strong>
              <p>Moins de pics à 12h30, plus de passages étalés sur le créneau 11h–14h.</p>
            </div>
            <div className="ru-benefit">
              <Zap size={32} className="ru-benefit-icon" />
              <strong>Réactivité immédiate</strong>
              <p>Un override prend effet en 0 secondes. Les étudiants voient le changement instantanément.</p>
            </div>
            <div className="ru-benefit">
              <Bell size={32} className="ru-benefit-icon" />
              <strong>Communication directe</strong>
              <p>Plus besoin d'affichettes : votre message atteint 5 000 étudiants en un clic.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 7. FINAL CTA */}
      <section className="ru-final-cta">
        <div className="ru-container">
          <div className="ru-text-center">
            <p className="ru-section-desc">
              Testez l'espace partenaire librement. Pas de durée minimum, pas de frais cachés. Vous restez tant que ça vous apporte de la valeur.
            </p>
            <div className="ru-cta-buttons">
              <Link to="/etablissement/demande-acces-partenaire" className="btn-brutal inverse-primary">
                Demander mon accès <ArrowRight size={20} />
              </Link>
              <a href="mailto:contact@crousradar.fr" className="btn-brutal secondary">
                Une question ? Écrivez-nous
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default RestaurantPage;
